
import { useState, useCallback } from 'react';
import { useTranslationUtils } from '@/hooks/useTranslationUtils';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Sweepstakes } from '@/hooks/useSweepstakesData';
import { removeImage } from '@/utils/imageUtils';
import { useFormValidation } from './useFormValidation';

export const useSweepstakesSubmit = (
  formValues: any, 
  editingSweepstakes: Sweepstakes | null,
  resetForm: () => void,
  fetchSweepstakes: () => Promise<void>
) => {
  const { t } = useTranslationUtils();
  const { toast } = useToast();
  const { validateForm } = useFormValidation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      
      // Validate form
      if (!validateForm(formValues)) {
        setIsSubmitting(false);
        return;
      }
      
      // Prepare sweepstakes data
      const startDate = new Date(formValues.start_date);
      const drawDate = new Date(formValues.draw_date);
      
      const sweepstakesData: any = {
        name: formValues.name,
        description: formValues.description,
        image_url: formValues.image_url,
        start_date: startDate.toISOString(),
        draw_date: drawDate.toISOString(),
        eligibility_criteria: formValues.eligibility_criteria,
        prize_details: formValues.prize_details
      };

      // If editing and the image has changed, remove the old image from storage
      if (editingSweepstakes && 
          'image_url' in editingSweepstakes && 
          editingSweepstakes.image_url && 
          editingSweepstakes.image_url !== formValues.image_url && 
          editingSweepstakes.image_url.includes('events/')) {
        try {
          await removeImage(editingSweepstakes.image_url, 'events');
        } catch (error) {
          console.error('Error removing old image:', error);
          // Don't block the form submission if image removal fails
        }
      }
      
      if (editingSweepstakes) {
        // Update existing sweepstakes
        const { error } = await supabase
          .from('sweepstakes')
          .update(sweepstakesData)
          .eq('id', editingSweepstakes.id);
          
        if (error) throw error;
        
        toast({
          title: t('admin.success'),
          description: t('admin.sweepstakesUpdated'),
        });
      } else {
        // Create new sweepstakes
        const { error } = await supabase
          .from('sweepstakes')
          .insert([sweepstakesData]);
          
        if (error) throw error;
        
        toast({
          title: t('admin.success'),
          description: t('admin.sweepstakesCreated'),
        });
      }
      
      // Reset form and refetch data
      resetForm();
      fetchSweepstakes();
    } catch (error) {
      console.error('Error saving sweepstakes:', error);
      toast({
        title: t('admin.error'),
        description: t('admin.errorSavingSweepstakes'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [formValues, editingSweepstakes, toast, t, resetForm, fetchSweepstakes, validateForm]);

  return { handleSubmit, isSubmitting };
};
